import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ListChecks, Plus, Trash2, Clock, TrendingUp, Briefcase, Flag, Timer, ArrowRight, Check, Pencil, X, CalendarArrowUp } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import GermanDateInput from "@/components/GermanDateInput";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ActivityCategoryManager from "@/components/ActivityCategoryManager";

const todayIso = () => new Date().toISOString().slice(0, 10);
const monthIso = () => new Date().toISOString().slice(0, 7);
const fmtMin = (m) => { const h = Math.floor(m / 60); const r = m % 60; return h ? `${h}h ${r ? r + "m" : ""}`.trim() : `${r || 0}m`; };
const fmtDateDe = (iso) => (iso && iso.length >= 10 ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : iso || "");
const MONTH_NAMES_DE = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
const fmtMonthDe = (ym) => {
  if (!ym || ym.length < 7) return ym || "";
  const [y, m] = ym.split("-");
  return `${MONTH_NAMES_DE[parseInt(m, 10) - 1] || m} ${y}`;
};
const fmtWeekDe = (isoWeek) => {
  const m = /^(\d{4})-W(\d{2})$/.exec(isoWeek || "");
  return m ? `KW ${m[2]}/${m[1]}` : (isoWeek || "");
};

function WeekGoalItem({ t, weekStartIso, onToggle, onChanged }) {
  const [edit, setEdit] = useState(false);
  const [title, setTitle] = useState(t.title);
  const save = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    try { await api.put(`/kpi/subtasks/${t.task_id}`, { title: title.trim() }); setEdit(false); onChanged(); toast.success("Wochenziel geändert"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  const postpone = async () => {
    const d = new Date(`${weekStartIso}T12:00:00`); d.setDate(d.getDate() + 7);
    try { await api.put(`/kpi/subtasks/${t.task_id}`, { week_start: d.toISOString().slice(0, 10) }); onChanged(); toast.success("In die nächste Woche verschoben"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  const remove = async () => {
    if (!window.confirm(`Wochenziel „${t.title}“ löschen?`)) return;
    try { await api.delete(`/kpi/subtasks/${t.task_id}`); onChanged(); toast.success("Wochenziel gelöscht"); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  if (edit) return (
    <li>
      <form onSubmit={save} className="flex items-center gap-2">
        <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Escape" && setEdit(false)} className="flex-1 bg-white/10 border border-white/30 px-2 py-1 text-sm text-white outline-none focus:border-[#00CCC9]" data-testid={`week-goal-edit-input-${t.task_id}`} />
        <button type="submit" className="p-1 text-[#00CCC9] hover:text-white" title="Speichern" data-testid={`week-goal-save-${t.task_id}`}><Check size={15} /></button>
        <button type="button" onClick={() => { setEdit(false); setTitle(t.title); }} className="p-1 text-slate-400 hover:text-white" title="Abbrechen" data-testid={`week-goal-cancel-${t.task_id}`}><X size={15} /></button>
      </form>
    </li>
  );
  return (
    <li className="group flex items-center gap-2">
      <button onClick={onToggle} className="flex items-center gap-2.5 text-sm hover:text-[#00CCC9] text-left" data-testid={`week-goal-${t.task_id}`}>
        <span className={`w-4 h-4 border flex items-center justify-center shrink-0 ${t.status === "done" ? "bg-[#00CCC9] border-[#00CCC9] text-slate-900" : "border-white/40"}`}>{t.status === "done" && <Check size={11} strokeWidth={3} />}</span>
        <span className={t.status === "done" ? "line-through text-slate-400" : ""}>{t.title}</span>
      </button>
      <span className="flex items-center gap-1 opacity-80 group-hover:opacity-100 transition-opacity">
        <button onClick={() => { setTitle(t.title); setEdit(true); }} className="p-1 text-slate-300 hover:text-[#00CCC9]" title="Bearbeiten" data-testid={`week-goal-edit-${t.task_id}`}><Pencil size={13} /></button>
        {t.status !== "done" && <button onClick={postpone} className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] text-slate-300 hover:text-[#00CCC9]" title="In die nächste Woche verschieben" data-testid={`week-goal-postpone-${t.task_id}`}><CalendarArrowUp size={13} /> Nächste Woche</button>}
        <button onClick={remove} className="p-1 text-slate-300 hover:text-red-400" title="Löschen" data-testid={`week-goal-delete-${t.task_id}`}><Trash2 size={13} /></button>
      </span>
    </li>
  );
}

const AMPEL_META = {
  green:  { label: "Ziel erreicht",  bar: "#10B981", chip: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  yellow: { label: "Im Rückstand",   bar: "#F59E0B", chip: "bg-amber-50 text-amber-800 border-amber-200" },
  red:    { label: "Kritisch",       bar: "#EF4444", chip: "bg-red-50 text-red-700 border-red-200" },
  gray:   { label: "Keine Daten",    bar: "#CBD5E1", chip: "bg-slate-50 text-slate-500 border-slate-200" },
};

function KpiProgressCard({ kpi }) {
  const meta = AMPEL_META[kpi.ampel] || AMPEL_META.gray;
  const pct = kpi.value != null ? Math.min(150, Math.round((100 * kpi.value) / (kpi.target || 1))) : 0;
  return (
    <div className="border border-slate-200 bg-white p-4" data-testid={`kpi-card-${kpi.id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="text-sm font-medium text-slate-900 leading-snug">{kpi.name}</div>
        <span className={`shrink-0 text-[9px] font-bold uppercase tracking-overline px-1.5 py-0.5 border ${meta.chip}`} data-testid={`kpi-ampel-${kpi.id}`}>
          {meta.label}
        </span>
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="font-heading text-2xl font-light tracking-tighter text-slate-900" data-testid={`kpi-value-${kpi.id}`}>
          {kpi.value != null ? kpi.value : "—"}
        </span>
        <span className="text-xs text-slate-500">/ {kpi.target} {kpi.unit} {kpi.period === "weekly" ? "· pro Woche" : "· pro Monat"}</span>
      </div>
      <div className="h-1.5 bg-slate-100 mt-2">
        <div className="h-full transition-[width] duration-500" style={{ width: `${Math.min(100, pct)}%`, background: meta.bar }} />
      </div>
      {kpi.overridden ? (
        <div className="text-[10px] text-slate-400 mt-1.5" data-testid={`kpi-overridden-${kpi.id}`}>Vom Management eingetragen{kpi.auto_value != null ? ` · Automatik-Vorschlag: ${kpi.auto_value}` : ""}</div>
      ) : kpi.source === "manual" ? (
        <div className="text-[10px] text-slate-400 mt-1.5">Manuell erfasst von Admin/Management</div>
      ) : kpi.derived_weekly ? (
        <div className="text-[10px] text-slate-400 mt-1.5">
          Wochenteilziel (Monatsziel ÷ 4) · {kpi.value != null ? `${pct}%` : "noch keine Daten"}
        </div>
      ) : (
        <div className="text-[10px] text-slate-400 mt-1.5">Automatisch gemessen · {kpi.value != null ? `${pct}%` : "noch keine Daten"}</div>
      )}
    </div>
  );
}

export default function KpiPage({ timeOnly = false }) {
  const { user } = useAuth();
  const [role, setRole] = useState(null);
  const [assigned, setAssigned] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [monthlyKpisForWeekView, setMonthlyKpisForWeekView] = useState(null);
  const [subtasks, setSubtasks] = useState([]);
  const [month, setMonth] = useState(monthIso());
  const [view, setView] = useState("month"); // "month" | "week"
  const [week, setWeek] = useState(() => {
    const d = new Date();
    // ISO week YYYY-Www
    const target = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    const dayNum = target.getUTCDay() || 7; target.setUTCDate(target.getUTCDate() + 4 - dayNum);
    const yearStart = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
    const wk = Math.ceil(((target - yearStart) / 86400000 + 1) / 7);
    return `${target.getUTCFullYear()}-W${String(wk).padStart(2, "0")}`;
  });

  const [timeSummary, setTimeSummary] = useState(null);
  const [timeEntries, setTimeEntries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [pieRange, setPieRange] = useState("day");
  const [acquiredClients, setAcquiredClients] = useState(null);
  const [acquiredListOpen, setAcquiredListOpen] = useState(false);
  const [entry, setEntry] = useState({ date: todayIso(), minutes: 30, category: "", task_id: "", meeting_type: "team", meeting_with: "", sub_activity: "", sub_activity_custom: "", quantity: "", note: "" });
  const [openResp, setOpenResp] = useState({});
  const [section, setSection] = useState("goals");
  const [substituting, setSubstituting] = useState({ active_substitutions: [], tasks: [] });
  const [isMaster, setIsMaster] = useState(false);
  const [reflexionOpen, setReflexionOpen] = useState(false);
  const [reflexion, setReflexion] = useState(null);
  const [catManagerOpen, setCatManagerOpen] = useState(false);

  const openReflexion = () => {
    setReflexionOpen(true);
    setReflexion(null);
    api.get("/kpi/weekly-reflection").then((r) => setReflexion(r.data)).catch((e) => toast.error(e.message));
  };
  const isAdmin = user?.role === "admin" || user?.role === "manager";
  const canManageCategories = user?.role === "admin" || isMaster;
  const reloadCategories = () => {
    api.get("/kpi/activity-categories").then((r) => setCategories(r.data || [])).catch(() => {});
  };

  const load = async () => {
    try {
      const params = view === "week" ? { week } : { month };
      const [r, d, ts, tfm, tsum, cats, acq] = await Promise.all([
        api.get("/kpi/my-role"),
        api.get("/kpi/dashboard", { params }),
        api.get("/kpi/subtasks"),
        api.get("/kpi/tasks-for-me").catch(() => ({ data: { active_substitutions: [], tasks: [] } })),
        api.get("/kpi/time-summary").catch(() => ({ data: null })),
        api.get("/kpi/activity-categories").catch(() => ({ data: [] })),
        api.get("/kpi/acquired-clients", { params: { month: monthIso() } }).catch(() => ({ data: null })),
      ]);
      setAssigned(!!r.data.assigned);
      setRole(r.data.role);
      setIsMaster(!!r.data.is_master_admin);
      setDashboard(d.data);
      setAcquiredClients(acq.data);
      // Week-Ansicht blendet monatliche KPIs aus (Backend liefert dort nur period='weekly').
      // Damit die Mitarbeiterin ihre Monatsziele nicht aus den Augen verliert, zusätzlich laden.
      if (view === "week") {
        api.get("/kpi/dashboard", { params: { month: monthIso() } })
          .then((r2) => setMonthlyKpisForWeekView(r2.data?.kpis || []))
          .catch(() => setMonthlyKpisForWeekView([]));
      } else {
        setMonthlyKpisForWeekView(null);
      }
      setSubtasks(ts.data || []);
      setSubstituting(tfm.data || { active_substitutions: [], tasks: [] });
      setTimeSummary(tsum.data);
      setCategories(cats.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [month, week, view]);

  useEffect(() => {
    const r = timeSummary?.[pieRange];
    if (!r) { setTimeEntries([]); return; }
    api.get("/kpi/time-entries", { params: { date_from: r.from, date_to: r.to } })
      .then((res) => setTimeEntries(res.data || [])).catch(() => {});
  }, [pieRange, timeSummary]);

  const weekStartIso = useMemo(() => {
    const d = new Date();
    const mon = new Date(d);
    mon.setDate(d.getDate() - ((d.getDay() + 6) % 7));
    return `${mon.getFullYear()}-${String(mon.getMonth() + 1).padStart(2, "0")}-${String(mon.getDate()).padStart(2, "0")}`;
  }, []);

  const weeklyGoals = useMemo(() => {
    const flagged = subtasks.filter((t) => t.is_weekly_goal);
    const relevant = flagged.filter((t) => (t.week_start ? t.week_start === weekStartIso : t.status !== "done" || ((t.updated_at || "").slice(0, 10) >= weekStartIso)));
    const done = relevant.filter((t) => t.status === "done").length;
    const total = relevant.length;
    const carry = flagged.filter((t) => t.status !== "done" && t.week_start && t.week_start < weekStartIso).length;
    return { total, done, open: total - done, pct: total ? Math.round((100 * done) / total) : 0, list: relevant, carry };
  }, [subtasks, weekStartIso]);

  const [newGoal, setNewGoal] = useState("");
  const addWeekGoal = async (e) => {
    e.preventDefault();
    if (!newGoal.trim()) return;
    try { await api.post("/kpi/subtasks", { title: newGoal.trim(), is_weekly_goal: true }); setNewGoal(""); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  const toggleWeekGoal = async (t) => {
    try { await api.put(`/kpi/subtasks/${t.task_id}`, { status: t.status === "done" ? "todo" : "done" }); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };
  const carryOver = async () => {
    try { const { data } = await api.post("/kpi/subtasks/carry-over"); toast.success(`${data.moved} Ziel(e) übernommen`); load(); }
    catch (err) { toast.error(err.message); }
  };

  const addTimeEntry = async () => {
    if (!entry.category) { toast.error("Tätigkeit wählen"); return; }
    if (!entry.minutes || entry.minutes < 1) { toast.error("Minuten eingeben"); return; }
    const selCat = categories.find((c) => c.id === entry.category);
    if (selCat?.sub_activities?.length && !entry.sub_activity) { toast.error(`Bitte eine Aufgabe für „${selCat.name}" wählen`); return; }
    if (selCat?.sub_activities?.length && entry.sub_activity === "custom" && !entry.sub_activity_custom.trim()) { toast.error("Bitte die eigene Aufgabe beschreiben"); return; }
    if (entry.sub_activity === "erstberatung" && (!entry.quantity || Number(entry.quantity) < 1)) { toast.error("Bitte die Anzahl der durchgeführten Erstberatungen angeben"); return; }
    try {
      const { mark_done: markDone, ...body } = entry;
      await api.post("/kpi/time-entries", { ...body, task_id: entry.task_id || null, quantity: entry.quantity ? parseInt(entry.quantity) : null });
      if (markDone && entry.task_id) await api.put(`/kpi/subtasks/${entry.task_id}`, { status: "done" });
      setEntry((e) => ({ ...e, minutes: 30, note: "", meeting_with: "", task_id: "", sub_activity: "", sub_activity_custom: "", quantity: "", mark_done: false }));
      toast.success("Zeit erfasst");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const removeTimeEntry = async (id) => {
    try { await api.delete(`/kpi/time-entries/${id}`); load(); } catch (e) { toast.error(e.message); }
  };

  if (!assigned && !timeOnly) {
    return (
      <div className="px-8 py-10 lg:px-12 lg:py-12 max-w-[900px] mx-auto" data-testid="kpi-not-assigned">
        <div className="border border-amber-300 bg-amber-50 p-8">
          <div className="text-xs font-bold uppercase tracking-overline text-amber-800 mb-2">Rolle noch nicht zugewiesen</div>
          <h1 className="font-heading text-3xl font-light tracking-tighter text-slate-900 mb-3">Keine WAAT-Rolle hinterlegt</h1>
          <p className="text-sm text-slate-700 mb-4">
            Ein Master-Admin muss dir zuerst eine Rolle aus dem Organigramm zuweisen (Coaches-Seite → Rolle wählen).
            Anschließend erscheinen hier deine Verantwortlichkeiten und KPIs.
          </p>
          <p className="text-sm text-slate-700 mb-4">Wochenziele und Zeiterfassung kannst du schon jetzt unter <a href="/entries" className="underline font-medium" data-testid="kpi-goto-entries">Mitarbeiterdokumentation</a> nutzen.</p>
          {isAdmin && (
            <a href="/coaches" data-testid="kpi-goto-coaches"
              className="inline-block px-4 py-2 bg-slate-900 text-white text-xs font-medium hover:bg-black">
              Zu Coaches · Rolle jetzt zuweisen
            </a>
          )}
        </div>
      </div>
    );
  }

  if (isMaster && !role && !timeOnly) {
    return (
      <div className="px-8 py-10 lg:px-12 lg:py-12 max-w-[1000px] mx-auto" data-testid="kpi-master-admin">
        <div className="flex items-end justify-between mb-6 border-b border-slate-200 pb-6 flex-wrap gap-3">
          <div>
            <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Meine Rolle & Ziele</div>
            <h1 className="font-heading text-4xl font-light tracking-tighter text-slate-900 mt-2">Master-Admin Übersicht</h1>
            <div className="flex items-center gap-2 mt-3">
              <span className="w-3 h-3 rounded-sm bg-slate-900" />
              <span className="text-sm text-slate-700 font-medium">Master-Admin · {user?.name}</span>
            </div>
          </div>
        </div>
        <div className="border border-slate-200 bg-white p-6 mb-6">
          <p className="text-sm text-slate-700 leading-relaxed">
            Als Master-Admin siehst du das komplette Portal — aber du hast selbst keine WAAT-Rolle mit
            KPIs. Um dir persönliche KPIs zu geben, weise dir auf <a href="/coaches" className="underline">/coaches</a>
            {" "}eine Rolle zu (z. B. „Geschäftsführung").
          </p>
          <Link to="/management" data-testid="kpi-master-goto-management"
            className="inline-flex items-center gap-1.5 mt-3 text-xs font-bold uppercase tracking-overline text-[#00807E] border border-[#00CCC9]/50 px-3 py-2 hover:bg-[#00CCC9] hover:text-slate-900 transition-colors">
            <TrendingUp size={13} strokeWidth={1.5} /> Zum Management-Bereich (KPIs & Team-Ampel) <ArrowRight size={12} strokeWidth={1.5} />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="px-8 py-10 lg:px-12 lg:py-12 max-w-[1300px] mx-auto" data-testid="kpi-page">
      {timeOnly && (
        <div className="mb-8" data-testid="mitarbeiterdoku-header">
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Mitarbeiterdokumentation</div>
          <h1 className="font-heading text-4xl font-light tracking-tighter text-slate-900 mt-1">Wofür nutze ich meine Zeit?</h1>
        </div>
      )}
      {!timeOnly && (<>
      {/* Header */}
      <div className="flex items-end justify-between mb-6 border-b border-slate-200 pb-6 flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-overline text-slate-500">Meine Rolle & Ziele</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-light tracking-tighter text-slate-900 mt-2">Meine Rolle & Ziele</h1>
          {role && (
            <div className="flex items-center gap-2 mt-3">
              <span className="w-3 h-3 rounded-sm" style={{ background: role.color }} />
              <span className="text-sm text-slate-700 font-medium">{role.name}</span>
            </div>
          )}
        </div>
        <div className="flex items-end gap-3">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Ansicht</label>
            <div className="flex border border-slate-300">
              <button onClick={() => setView("month")} data-testid="view-month"
                className={`px-3 py-1.5 text-xs font-medium ${view === "month" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>Monat</button>
              <button onClick={() => setView("week")} data-testid="view-week"
                className={`px-3 py-1.5 text-xs font-medium border-l border-slate-300 ${view === "week" ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>Woche</button>
            </div>
          </div>
          {view === "month" ? (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Monat</label>
              <input type="month" className="input-tech font-mono" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="kpi-month" />
            </div>
          ) : (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Woche (ISO)</label>
              <input type="week" className="input-tech font-mono" value={week} onChange={(e) => setWeek(e.target.value)} data-testid="kpi-week" />
            </div>
          )}
        </div>
      </div>

      {/* Section tabs */}
      <div className="flex border-b border-slate-200 mb-6 gap-1 flex-wrap" data-testid="pm-tabs">
        {[
          { k: "goals",   l: "Aufgabenerfassung", icon: ListChecks },
          { k: "role",    l: "Aufgabenbereich",   icon: Briefcase },
        ].map(({ k, l, icon: Ico }) => (
          <button key={k} onClick={() => setSection(k)} data-testid={`pm-tab-${k}`}
            className={`px-4 py-2.5 text-xs font-medium flex items-center gap-2 border-b-2 ${section === k ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-900"}`}>
            <Ico size={12} strokeWidth={1.5} /> {l}
          </button>
        ))}
      </div>

      {/* SECTION: Aufgabenbereich (Rolle + Verantwortlichkeiten) */}
      {section === "role" && (
        <div className="border border-slate-200 bg-white p-6" data-testid="section-role">
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500">Funktionsbeschreibung</div>
          <h2 className="font-heading text-2xl font-light tracking-tighter text-slate-900 mt-1 mb-4">{role?.name}</h2>
          <div className="text-xs text-slate-600 mb-4">Alle Verantwortlichkeiten deiner Rolle laut WAAT-Organigramm.</div>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {(dashboard?.responsibilities || []).map((r) => (
              <li key={r.id} className="border border-slate-200 px-4 py-3" data-testid={`role-resp-${r.id}`}>
                <div className="text-sm font-medium text-slate-900">{r.name}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* SECTION: Wochenziele – Kanban Todo/Doing/Done + Delegate + Deadline */}
      {section === "goals" && (
      <>
      {substituting.active_substitutions && substituting.active_substitutions.length > 0 && (
        <div className="mb-6 border border-purple-300 bg-purple-50 p-4 text-xs" data-testid="substituting-banner">
          <div className="font-bold text-purple-900 mb-1">Du vertrittst gerade:</div>
          <div className="text-purple-800">
            {substituting.active_substitutions.map((s) => s.name).join(" · ")} — deren offene Aufgaben erscheinen unten
            mit einem <span className="px-1 py-0.5 border border-purple-300 bg-white text-purple-800">Vertretung</span> Badge.
            Bei Geschäftsführung nur die explizit delegierten.
          </div>
        </div>
      )}

      {/* KPI-Fortschritt: die tatsächlichen Ziel-Kennzahlen der Rolle, visuell + Kuchen-Diagramm */}
      <div className="mb-6 border border-slate-200 bg-white p-5" data-testid="kpi-progress-section">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2">
              <TrendingUp size={11} strokeWidth={1.5} /> KPI-Fortschritt
            </div>
            <div className="font-heading text-xl font-light tracking-tighter text-slate-900">
              Wie weit sind meine Ziele erreicht? · {view === "week" ? fmtWeekDe(week) : fmtMonthDe(month)}
            </div>
          </div>
          {dashboard?.totals && (dashboard.kpis || []).length > 0 && (
            <div className="flex items-center gap-4">
              <div className="relative w-[76px] h-[76px]" data-testid="kpi-totals-pie">
                <ResponsiveContainer width="100%" height={76}>
                  <PieChart>
                    <Pie
                      data={["green", "yellow", "red", "gray"].map((k) => ({ name: k, value: dashboard.totals[k] || 0 }))}
                      innerRadius={24} outerRadius={38} paddingAngle={2} dataKey="value" stroke="none">
                      {["green", "yellow", "red", "gray"].map((k) => <Cell key={k} fill={AMPEL_META[k].bar} />)}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <ul className="text-[11px] space-y-1">
                {["green", "yellow", "red", "gray"].map((k) => (
                  <li key={k} className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: AMPEL_META[k].bar }} />
                    <span className="text-slate-700">{dashboard.totals[k] || 0} × {AMPEL_META[k].label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        {(() => {
          const weeklyKpis = dashboard?.kpis || [];
          const monthlyExtra = view === "week"
            ? (monthlyKpisForWeekView || []).filter((mk) => mk.period !== "weekly" && !weeklyKpis.some((wk) => wk.id === mk.id))
            : [];
          const kpisToShow = [...weeklyKpis, ...monthlyExtra];
          if (kpisToShow.length === 0) {
            return (
              <div className="text-sm text-slate-400 italic text-center py-10" data-testid="kpi-progress-empty">
                Keine KPIs für deine Rolle hinterlegt.
              </div>
            );
          }
          return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="kpi-progress-grid">
              {kpisToShow.map((k) => <KpiProgressCard key={k.id} kpi={k} />)}
            </div>
          );
        })()}
        {view === "week" && (monthlyKpisForWeekView || []).some((mk) => mk.period !== "weekly") && (
          <div className="text-[10px] text-slate-400 mt-3">
            Monatsziele werden hier zusätzlich zur Wochenansicht angezeigt, damit du sie nicht aus den Augen verlierst.
          </div>
        )}
      </div>

      {acquiredClients && (
        <div className="mb-6 border border-slate-200 bg-white p-5" data-testid="acquired-clients-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2">
                <Briefcase size={11} strokeWidth={1.5} /> Von mir gewonnene Kunden
              </div>
              <div className="font-heading text-xl font-light tracking-tighter text-slate-900">
                {acquiredClients.total_count} Kunde{acquiredClients.total_count === 1 ? "" : "n"} insgesamt
                <span className="text-sm text-slate-500 font-sans"> · {acquiredClients.month_count} in {fmtMonthDe(acquiredClients.month)}</span>
              </div>
            </div>
            {acquiredClients.total_count > 0 && (
              <button onClick={() => setAcquiredListOpen((v) => !v)} data-testid="acquired-clients-toggle"
                className="text-[10px] font-medium text-[#00807E] hover:underline">
                {acquiredListOpen ? "Liste ausblenden" : "Liste anzeigen"}
              </button>
            )}
          </div>
          {acquiredListOpen && acquiredClients.clients?.length > 0 && (
            <ul className="mt-4 divide-y divide-slate-100" data-testid="acquired-clients-list">
              {acquiredClients.clients.map((c) => (
                <li key={c.client_id} className="py-2 flex items-center justify-between text-xs">
                  <span className="text-slate-800">{c.name}</span>
                  <span className="text-slate-500 font-mono">{c.total_ue} UE · {fmtDateDe((c.created_at || "").slice(0, 10))}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      </>)}
      </>)}
      {(timeOnly || section === "goals") && (<>
      {/* Wochenziel-Fortschritt */}
      <div className="mb-6 relative overflow-hidden bg-slate-900 text-white p-6" data-testid="weekly-progress-widget">
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#00CCC9]/10 blur-2xl pointer-events-none" />
        <div className="flex items-end justify-between flex-wrap gap-4 relative">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-overline text-[#00CCC9] flex items-center gap-2">
              <Flag size={11} strokeWidth={1.5} /> Diese Woche
            </div>
            <div className="font-heading text-3xl font-light tracking-tighter mt-1" data-testid="weekly-progress-text">
              {weeklyGoals.done} von {weeklyGoals.total} Wochenzielen erledigt
            </div>
            <div className="text-xs text-slate-400 mt-1">
              {weeklyGoals.total === 0 ? "Noch keine Wochenziele markiert — leg los!" : weeklyGoals.open === 0 ? "Alle Ziele geschafft — stark!" : `Noch ${weeklyGoals.open} offen — du schaffst das!`}
            </div>
          </div>
          <div className="text-right">
            <div className="font-heading text-5xl font-light text-[#00CCC9]" data-testid="weekly-progress-pct">{weeklyGoals.pct}%</div>
            <button onClick={openReflexion}
              className="mt-1 text-[10px] font-bold uppercase tracking-overline px-2.5 py-1.5 border border-[#00CCC9]/50 text-[#00CCC9] hover:bg-[#00CCC9] hover:text-slate-900 transition-colors"
              data-testid="open-reflexion-btn">
              Wochen-Reflexion ansehen
            </button>
          </div>
        </div>
        <div className="h-2 bg-white/10 mt-4">
          <div className="h-full bg-gradient-to-r from-[#00CCC9] to-emerald-400 transition-[width] duration-700" style={{ width: `${weeklyGoals.pct}%` }} />
        </div>
        <ul className="mt-4 space-y-1 relative" data-testid="week-goal-list">
          {weeklyGoals.list.map((t) => (
            <WeekGoalItem key={t.task_id} t={t} weekStartIso={weekStartIso} onToggle={() => toggleWeekGoal(t)} onChanged={load} />
          ))}
        </ul>
        <form onSubmit={addWeekGoal} className="mt-3 flex gap-2 relative">
          <input value={newGoal} onChange={(e) => setNewGoal(e.target.value)} placeholder="+ Wochenziel hinzufügen, z. B. „3 Zwischenberichte fertig“" className="flex-1 bg-white/10 border border-white/20 px-3 py-2 text-sm text-white placeholder:text-slate-400 outline-none focus:border-[#00CCC9]" data-testid="week-goal-input" />
          <button disabled={!newGoal.trim()} className="px-4 text-xs font-bold uppercase tracking-overline bg-[#00CCC9] text-slate-900 disabled:opacity-40" data-testid="week-goal-add">Hinzufügen</button>
        </form>
        {weeklyGoals.carry > 0 && (
          <button onClick={carryOver} className="mt-2 text-[11px] text-[#00CCC9] underline underline-offset-2 relative" data-testid="week-goal-carry">{weeklyGoals.carry} offene(s) Ziel(e) aus der Vorwoche übernehmen</button>
        )}
      </div>

      {/* Wochen-Reflexion Dialog */}
      <Dialog open={reflexionOpen} onOpenChange={setReflexionOpen}>
        <DialogContent className="max-w-lg bg-white max-h-[85vh] overflow-y-auto" data-testid="reflexion-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-light tracking-tighter">Deine Wochen-Reflexion</DialogTitle>
          </DialogHeader>
          {!reflexion ? (
            <div className="text-sm text-slate-400 italic text-center py-8">Lädt…</div>
          ) : (
            <div className="space-y-4">
              <div className="text-xs text-slate-500">Woche {fmtDateDe(reflexion.from)} – {fmtDateDe(reflexion.to)}</div>
              <div>
                <div className="font-heading text-3xl font-light" data-testid="reflexion-headline">
                  {reflexion.done.length} von {reflexion.total} Wochenzielen erledigt · <span className="text-[#00807E]">{reflexion.pct}%</span>
                </div>
                <div className="h-2 bg-slate-100 mt-2">
                  <div className="h-full bg-gradient-to-r from-[#00CCC9] to-emerald-400" style={{ width: `${Math.min(100, reflexion.pct)}%` }} />
                </div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1.5">Erledigt</div>
                <ul className="space-y-1 text-sm" data-testid="reflexion-done-list">
                  {reflexion.done.length === 0 && <li className="text-slate-400 italic">Noch nichts erledigt.</li>}
                  {reflexion.done.map((t, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-800">
                      <span className="text-emerald-600">✓</span> {t.title}
                      {t.kpi_name && <span className="text-[10px] text-slate-400 italic">· {t.kpi_name}</span>}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1.5">Noch offen</div>
                <ul className="space-y-1 text-sm" data-testid="reflexion-open-list">
                  {reflexion.open.length === 0 && <li className="text-emerald-700">Alles geschafft — stark! 🎉</li>}
                  {reflexion.open.map((t, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-600">
                      <span className="w-3 h-3 border border-slate-300 inline-block shrink-0" /> {t.title}
                      {t.deadline && <span className="text-[10px] text-slate-400 font-mono">bis {fmtDateDe(t.deadline)}</span>}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1.5">
                  Zeit-Bilanz · gesamt {fmtMin(reflexion.time?.total_minutes || 0)}
                </div>
                <ul className="space-y-1 text-xs" data-testid="reflexion-time-list">
                  {(reflexion.time?.by_category || []).length === 0 && <li className="text-slate-400 italic">Keine Zeit erfasst.</li>}
                  {(reflexion.time?.by_category || []).map((c) => (
                    <li key={c.category} className="flex items-center gap-2">
                      <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: c.color }} />
                      <span className="flex-1 text-slate-800">{c.name}</span>
                      <span className="font-mono text-slate-600">{fmtMin(c.minutes)}</span>
                    </li>
                  ))}
                </ul>
                {reflexion.time?.auto_coaching?.minutes > 0 && (
                  <div className="text-[10px] text-slate-500 mt-1.5">
                    Inkl. {reflexion.time.auto_coaching.count} Coaching-Termine ({fmtMin(reflexion.time.auto_coaching.minutes)}) automatisch gebucht.
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Zeit-Verteilung (Kuchen Tag/Woche/Monat) + Zeit erfassen */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="border border-slate-200 bg-white p-5" data-testid="time-pie-card">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2"><Clock size={11} strokeWidth={1.5} /> Zeit-Verteilung</div>
              <div className="font-heading text-xl font-light tracking-tighter text-slate-900">Wofür nutze ich meine Zeit?</div>
            </div>
            <div className="flex border border-slate-300" data-testid="pie-range-toggle">
              {[["day", "Tag"], ["week", "Woche"], ["month", "Monat"]].map(([k, l]) => (
                <button key={k} onClick={() => setPieRange(k)} data-testid={`pie-range-${k}`}
                  className={`px-3 py-1.5 text-xs font-medium ${k !== "day" ? "border-l border-slate-300" : ""} ${pieRange === k ? "bg-slate-900 text-white" : "bg-white text-slate-700 hover:bg-slate-50"}`}>{l}</button>
              ))}
            </div>
          </div>
          {(() => {
            const agg = timeSummary?.[pieRange];
            const data = (agg?.by_category || []).map((c) => ({ name: c.name, value: c.minutes, color: c.color }));
            if (!data.length) return <div className="text-sm text-slate-400 italic text-center py-16" data-testid="pie-empty">Noch keine Zeit erfasst {pieRange === "day" ? "heute" : pieRange === "week" ? "diese Woche" : "diesen Monat"}.</div>;
            const total = agg.total_minutes || 1;
            const weekTargetMin = (timeSummary?.weekly_hours_target || 0) * 60;
            return (
              <>
              <div className="grid grid-cols-1 sm:grid-cols-[220px_1fr] gap-4 items-center mt-4">
                <div className="relative">
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={data} innerRadius={62} outerRadius={95} paddingAngle={2} dataKey="value" stroke="none">
                        {data.map((e, i) => <Cell key={i} fill={e.color} />)}
                      </Pie>
                      <Tooltip formatter={(v) => fmtMin(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <div className="font-heading text-2xl font-light text-slate-900">{fmtMin(agg.total_minutes)}</div>
                    <div className="text-[9px] uppercase tracking-overline text-slate-400">gesamt</div>
                  </div>
                </div>
                <ul className="space-y-1.5 text-xs max-h-[220px] overflow-y-auto pr-1">
                  {data.map((d) => (
                    <li key={d.name} className="flex items-center gap-2">
                      <span className="w-3 h-3 shrink-0 rounded-sm" style={{ background: d.color }} />
                      <span className="flex-1 text-slate-900 truncate">{d.name}</span>
                      <span className="font-mono text-slate-600">{fmtMin(d.value)}</span>
                      <span className="font-mono text-slate-400 text-[10px] w-9 text-right">{Math.round((100 * d.value) / total)}%</span>
                    </li>
                  ))}
                </ul>
              </div>
              {agg.auto_coaching?.minutes > 0 && (
                <div className="mt-3 flex items-center gap-2 text-[11px] text-slate-600 border border-[#00CCC9]/40 bg-[#00CCC9]/5 px-3 py-2" data-testid="pie-auto-coaching">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: "#00CCC9" }} />
                  <span>Inkl. <strong>{agg.auto_coaching.count} Coaching-Termin{agg.auto_coaching.count === 1 ? "" : "e"}</strong> ({fmtMin(agg.auto_coaching.minutes)}) — automatisch aus deinen unterschriebenen/abgeschlossenen Terminen gebucht.</span>
                </div>
              )}
              {pieRange === "week" && weekTargetMin > 0 && (
                <div className="mt-3" data-testid="pie-week-target">
                  <div className="flex items-baseline justify-between text-[11px] text-slate-600 mb-1">
                    <span>Wochenarbeitszeit erfasst</span>
                    <span className="font-mono">{fmtMin(agg.total_minutes)} / {fmtMin(weekTargetMin)} · {Math.min(999, Math.round((100 * agg.total_minutes) / weekTargetMin))}%</span>
                  </div>
                  <div className="h-1.5 bg-slate-100">
                    <div className="h-full bg-slate-900 transition-[width] duration-500" style={{ width: `${Math.min(100, (100 * agg.total_minutes) / weekTargetMin)}%` }} />
                  </div>
                </div>
              )}
              </>
            );
          })()}
        </div>

        <div id="time-entry-card" className="border border-slate-200 bg-white p-5" data-testid="time-entry-card">
          <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2"><Timer size={11} strokeWidth={1.5} /> Aufgaben-Erfassung</div>
          <div className="font-heading text-xl font-light tracking-tighter text-slate-900 mb-4">Erledigte Aufgabe + Zeit eintragen</div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Datum</label>
              <GermanDateInput className="input-tech font-mono w-full" value={entry.date} onChange={(e) => setEntry({ ...entry, date: e.target.value })} data-testid="entry-date" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Minuten</label>
              <div className="flex items-center gap-1">
                <input type="number" min="1" max="1440" className="input-tech font-mono w-full" value={entry.minutes} onChange={(e) => setEntry({ ...entry, minutes: parseInt(e.target.value) || 0 })} data-testid="entry-minutes" />
                {[15, 30, 60].map((m) => (
                  <button key={m} onClick={() => setEntry((x) => ({ ...x, minutes: m }))} className="text-[10px] font-medium px-1.5 py-1.5 border border-slate-300 text-slate-700 hover:border-slate-900 hover:bg-slate-900 hover:text-white transition-colors" data-testid={`entry-quick-${m}`}>{m}</button>
                ))}
              </div>
            </div>
            <div className="col-span-2">
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 block">Tätigkeit</label>
                {canManageCategories && (
                  <button type="button" onClick={() => setCatManagerOpen(true)} data-testid="manage-categories-button"
                    className="text-[10px] font-medium text-[#00807E] hover:underline">Verwalten</button>
                )}
              </div>
              <select className="input-tech w-full" value={entry.category} onChange={(e) => setEntry({ ...entry, category: e.target.value })} data-testid="entry-category">
                <option value="">— Tätigkeit wählen —</option>
                {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            {entry.category === "meeting" && (
              <div className="col-span-2 border border-indigo-200 bg-indigo-50/50 p-3 space-y-2" data-testid="entry-meeting-extra">
                <div className="flex items-center gap-4 text-xs">
                  {[["team", "Team-Sitzung"], ["strategie", "Strategie-Sitzung"]].map(([k, l]) => (
                    <label key={k} className="flex items-center gap-1.5 cursor-pointer">
                      <input type="radio" name="meeting_type" checked={entry.meeting_type === k} onChange={() => setEntry({ ...entry, meeting_type: k })} data-testid={`entry-meeting-${k}`} />
                      <span className="text-slate-800">{l}</span>
                    </label>
                  ))}
                </div>
                <input className="input-tech text-xs w-full" placeholder="Mit wem? (z. B. Ana, Christoph …)" value={entry.meeting_with} onChange={(e) => setEntry({ ...entry, meeting_with: e.target.value })} data-testid="entry-meeting-with" />
              </div>
            )}
            {(() => {
              const selCat = categories.find((c) => c.id === entry.category);
              if (!selCat?.sub_activities?.length) return null;
              return (
                <div className="col-span-2 border border-emerald-200 bg-emerald-50/50 p-3 space-y-2" data-testid="entry-tn-extra">
                  <label className="text-[10px] font-bold uppercase tracking-overline text-emerald-800 block">{selCat.name} · Aufgabe</label>
                  <select className="input-tech text-xs w-full" value={entry.sub_activity}
                    onChange={(e) => setEntry({ ...entry, sub_activity: e.target.value })} data-testid="entry-tn-subactivity">
                    <option value="">— Aufgabe wählen —</option>
                    {selCat.sub_activities.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                  {entry.sub_activity === "custom" && (
                    <input className="input-tech text-xs w-full" placeholder="Eigene Aufgabe beschreiben …"
                      value={entry.sub_activity_custom}
                      onChange={(e) => setEntry({ ...entry, sub_activity_custom: e.target.value })}
                      data-testid="entry-tn-custom" />
                  )}
                  {entry.sub_activity === "erstberatung" && (
                    <div>
                      <label className="text-[10px] font-bold uppercase tracking-overline text-emerald-800 mb-1 block">Anzahl durchgeführter Erstberatungen *</label>
                      <input type="number" min="1" step="1" className="input-tech text-xs w-full" placeholder="z. B. 1"
                        value={entry.quantity}
                        onChange={(e) => setEntry({ ...entry, quantity: e.target.value })}
                        data-testid="entry-erstberatung-quantity" />
                    </div>
                  )}
                </div>
              );
            })()}
            <div className="col-span-2">
              <label className="text-[10px] font-bold uppercase tracking-overline text-slate-500 mb-1 block">Aufgabe verknüpfen (optional)</label>
              <select className="input-tech w-full" value={entry.task_id} onChange={(e) => setEntry({ ...entry, task_id: e.target.value, mark_done: false })} data-testid="entry-task">
                <option value="">— keine Aufgabe —</option>
                {weeklyGoals.list.filter((t) => t.status !== "done").length > 0 && (
                  <optgroup label="Wochenziele">{weeklyGoals.list.filter((t) => t.status !== "done").map((t) => <option key={t.task_id} value={t.task_id}>★ {t.title}</option>)}</optgroup>
                )}
                <optgroup label="Weitere Aufgaben">{subtasks.filter((t) => !weeklyGoals.list.includes(t)).map((t) => <option key={t.task_id} value={t.task_id}>{t.title}</option>)}</optgroup>
              </select>
              {weeklyGoals.list.some((t) => t.task_id === entry.task_id && t.status !== "done") && (
                <label className="mt-1.5 flex items-center gap-2 text-xs text-slate-700" data-testid="entry-mark-done-wrap">
                  <input type="checkbox" checked={!!entry.mark_done} onChange={(e) => setEntry({ ...entry, mark_done: e.target.checked })} data-testid="entry-mark-done" /> Ziel damit erledigt
                </label>
              )}
            </div>
            <div className="col-span-2">
              <input className="input-tech text-sm w-full" placeholder="Notiz (optional)" value={entry.note} onChange={(e) => setEntry({ ...entry, note: e.target.value })} data-testid="entry-note" />
            </div>
            <div className="col-span-2">
              <button onClick={addTimeEntry} data-testid="entry-save"
                className="w-full px-4 py-2.5 bg-slate-900 text-white text-xs font-medium hover:bg-black flex items-center justify-center gap-1.5 transition-colors">
                <Plus size={11} strokeWidth={2} /> Zeit buchen
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Erfasste Zeiten (aktueller Bereich) */}
      {timeEntries.length > 0 && (
        <div className="border border-slate-200 bg-white mb-8" data-testid="time-entries-list">
          <div className="p-5 border-b border-slate-200">
            <div className="text-[10px] font-bold uppercase tracking-overline text-slate-500 flex items-center gap-2"><Clock size={11} strokeWidth={1.5} /> Erfasste Zeiten · {pieRange === "day" ? "Heute" : pieRange === "week" ? "Diese Woche" : "Dieser Monat"}</div>
          </div>
          <ul className="divide-y divide-slate-100">
            {timeEntries.map((e) => {
              const cat = categories.find((c) => c.id === e.category);
              const task = subtasks.find((t) => t.task_id === e.task_id);
              return (
                <li key={e.entry_id} className="p-3 flex items-center gap-3 flex-wrap" data-testid={`time-entry-${e.entry_id}`}>
                  <span className="font-mono text-[10px] text-slate-500">{fmtDateDe(e.date)}</span>
                  <span className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-900">
                    <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: cat?.color || "#94A3B8" }} />
                    {cat?.name || e.category}
                  </span>
                  {e.category === "meeting" && (
                    <span className="text-[10px] px-1.5 py-0.5 border border-indigo-200 bg-indigo-50 text-indigo-800">
                      {e.meeting_type === "strategie" ? "Strategie-Sitzung" : "Team-Sitzung"}{e.meeting_with ? ` · mit ${e.meeting_with}` : ""}
                    </span>
                  )}
                  {e.sub_activity && (
                    <span className="text-[10px] px-1.5 py-0.5 border border-emerald-200 bg-emerald-50 text-emerald-800" data-testid={`time-entry-sub-${e.entry_id}`}>
                      {e.sub_activity === "custom"
                        ? e.sub_activity_custom
                        : (categories.find((c) => c.id === e.category)?.sub_activities || []).find((s) => s.id === e.sub_activity)?.name || e.sub_activity}
                    </span>
                  )}
                  {task && <span className="text-[10px] text-slate-500 italic truncate max-w-[220px]">→ {task.title}</span>}
                  {e.note && <span className="text-[10px] text-slate-400 truncate max-w-[180px]">{e.note}</span>}
                  <div className="flex-1" />
                  <span className="font-mono text-sm text-slate-900">{fmtMin(e.minutes)}</span>
                  <button onClick={() => removeTimeEntry(e.entry_id)} className="text-slate-400 hover:text-red-600" data-testid={`time-entry-del-${e.entry_id}`}><Trash2 size={12} strokeWidth={1.5} /></button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
      </>
      )}
      {canManageCategories && (
        <ActivityCategoryManager open={catManagerOpen} onOpenChange={setCatManagerOpen}
          categories={categories} onChanged={reloadCategories} />
      )}
    </div>
  );
}
